import React, { useState } from "react";
import PageHeader from "../components/PageHeader";
import { useTeam } from "../../hooks/useFirebase";
import { addTeamMember } from "../../firebase/authService";
import { db } from "../../firebase/config";
import {
  doc,
  updateDoc,
  deleteDoc,
  serverTimestamp,
} from "firebase/firestore";
import { deleteUser } from "firebase/auth";

const TeamPage = () => {
  const { teamMembers, loading, refreshTeam } = useTeam();

  // ========== STATES ==========
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [viewMember, setViewMember] = useState(null);
  const [memberToDelete, setMemberToDelete] = useState(null);
  const [modalLoading, setModalLoading] = useState(false);
  const [modalError, setModalError] = useState("");
  const [modalSuccess, setModalSuccess] = useState("");

  // ========== SEARCH & FILTER ==========
  const [searchTerm, setSearchTerm] = useState("");
  const [filterRole, setFilterRole] = useState("all");

  // ========== SHOW PASSWORD STATES ==========
  const [showAddPassword, setShowAddPassword] = useState(false);
  const [showEditPassword, setShowEditPassword] = useState(false);
  const [showViewPassword, setShowViewPassword] = useState(false);

  // ========== ADD FORM ==========
  const [addForm, setAddForm] = useState({
    name: "", email: "", password: "", phone: "", role: "",
  });

  // ========== EDIT FORM ==========
  const [editForm, setEditForm] = useState({
    id: "", name: "", email: "", password: "", phone: "", role: "", isActive: true,
  });

  const handleAddChange = (e) => {
    setAddForm((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleEditChange = (e) => {
    setEditForm((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  // ========== ADD MEMBER ==========
  const handleAddMember = async () => {
    if (!addForm.name || !addForm.email || !addForm.password || !addForm.role) {
      setModalError("Name, Email, Password aur Role sab required hain!");
      return;
    }
    if (addForm.password.length < 6) {
      setModalError("Password kam se kam 6 characters hona chahiye!");
      return;
    }

    setModalLoading(true);
    setModalError("");

    try {
      const result = await addTeamMember({
        name: addForm.name,
        email: addForm.email,
        password: addForm.password,
        role: addForm.role,
        phone: addForm.phone,
      });

      console.log("✅ Member added:", result);
      setModalSuccess(`✅ "${addForm.name}" added! Login details: ${addForm.email} / ${addForm.password}`);

      // Don't reset immediately, show success
      setAddForm({ name: "", email: "", password: "", phone: "", role: "" });
      refreshTeam();

      setTimeout(() => {
        setShowAddModal(false);
        setModalSuccess("");
      }, 3000);
    } catch (error) {
      setModalError(error.message);
    } finally {
      setModalLoading(false);
    }
  };

  // ========== EDIT MEMBER ==========
  const openEditModal = (member) => {
    setEditForm({
      id: member.id,
      name: member.name || "",
      email: member.email || "",
      password: member.password || "",
      phone: member.phone || "",
      role: member.role || "sales",
      isActive: member.isActive !== false,
    });
    setModalError("");
    setModalSuccess("");
    setShowEditModal(true);
  };

  const handleEditMember = async () => {
    if (!editForm.name || !editForm.email || !editForm.role) {
      setModalError("Name, Email aur Role required hain!");
      return;
    }

    setModalLoading(true);
    setModalError("");

    try {
      await updateDoc(doc(db, "users", editForm.id), {
        name: editForm.name,
        email: editForm.email,
        password: editForm.password,
        phone: editForm.phone,
        role: editForm.role,
        isActive: editForm.isActive,
        updatedAt: serverTimestamp(),
      });

      // Permissions update
      const newPerms = getPermissionsForRole(editForm.role);
      await updateDoc(doc(db, "users", editForm.id), {
        permissions: newPerms,
      });

      setModalSuccess(`✅ "${editForm.name}" updated successfully!`);
      refreshTeam();

      setTimeout(() => {
        setShowEditModal(false);
        setModalSuccess("");
      }, 2000);
    } catch (error) {
      setModalError("Failed: " + error.message);
    } finally {
      setModalLoading(false);
    }
  };

  // ========== VIEW MEMBER ==========
  const openViewModal = (member) => {
    setViewMember(member);
    setShowViewPassword(false);
    setShowViewModal(true);
  };

  // ========== DELETE MEMBER ==========
  const openDeleteModal = (member) => {
    setMemberToDelete(member);
    setShowDeleteModal(true);
  };

  const handleDeleteMember = async () => {
    if (!memberToDelete) return;
    setModalLoading(true);
    try {
      await deleteDoc(doc(db, "users", memberToDelete.id));
      setMemberToDelete(null);
      setShowDeleteModal(false);
      refreshTeam();
    } catch (error) {
      setModalError("Failed: " + error.message);
    } finally {
      setModalLoading(false);
    }
  };

  // ========== HELPERS ==========
  function getPermissionsForRole(role) {
    const perms = {
      admin: { viewLeads: true, addLeads: true, editLeads: true, deleteLeads: true, viewCalls: true, addCalls: true, viewTasks: true, manageTasks: true, viewReports: true, manageTeam: true, accountingAccess: true, settingsAccess: true },
      manager: { viewLeads: true, addLeads: true, editLeads: true, deleteLeads: false, viewCalls: true, addCalls: true, viewTasks: true, manageTasks: true, viewReports: true, manageTeam: false, accountingAccess: false, settingsAccess: false },
      sales: { viewLeads: true, addLeads: true, editLeads: false, deleteLeads: false, viewCalls: true, addCalls: true, viewTasks: true, manageTasks: false, viewReports: false, manageTeam: false, accountingAccess: false, settingsAccess: false },
      accountant: { viewLeads: false, addLeads: false, editLeads: false, deleteLeads: false, viewCalls: false, addCalls: false, viewTasks: true, manageTasks: true, viewReports: true, manageTeam: false, accountingAccess: true, settingsAccess: false },
    };
    return perms[role] || perms.sales;
  }

  const roleDescriptions = {
    admin: "✅ Full access: Leads, Calls, Tasks, Reports, Team, Accounting, Settings",
    manager: "📋 Access: Leads, Calls, Tasks, Reports (no team/accounting)",
    sales: "📞 Access: Own Tasks only (Employee Dashboard)",
    accountant: "💰 Access: Accounting, Tasks, Reports",
  };

  // ========== FILTERED ==========
  const filteredMembers = teamMembers.filter((m) => {
    const matchesSearch =
      m.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      m.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      m.phone?.includes(searchTerm);
    const matchesRole = filterRole === "all" || m.role === filterRole;
    return matchesSearch && matchesRole;
  });

  // ========== PASSWORD STRENGTH ==========
  const getPasswordStrength = (pwd) => {
    if (pwd.length >= 10) return { label: "Very Strong", color: "#16a34a", width: "100%" };
    if (pwd.length >= 8) return { label: "Strong", color: "#22c55e", width: "80%" };
    if (pwd.length >= 6) return { label: "Medium", color: "#f97316", width: "60%" };
    return { label: "Weak", color: "#ef4444", width: "30%" };
  };

  // ==========================================
  // RENDER
  // ==========================================
  return (
    <div>
      <PageHeader title="Team Management" breadcrumbs={["Admin", "Team"]} />

      {/* Stats */}
      <div className="admin-grid admin-grid-4" style={{ marginTop: 24, marginBottom: 24 }}>
        {[
          { label: "Total Members", value: teamMembers.length, icon: "👥", cls: "qs-blue" },
          { label: "Active", value: teamMembers.filter((m) => m.isActive !== false).length, icon: "✅", cls: "qs-green" },
          { label: "Sales Team", value: teamMembers.filter((m) => m.role === "sales").length, icon: "📞", cls: "qs-orange" },
          { label: "Managers", value: teamMembers.filter((m) => m.role === "manager").length, icon: "👔", cls: "qs-purple" },
        ].map((s, i) => (
          <div key={i} className={`quick-stat ${s.cls}`}>
            <div className="quick-stat-icon">{s.icon}</div>
            <div className="quick-stat-info">
              <div className="quick-stat-label">{s.label}</div>
              <div className="quick-stat-value">{s.value}</div>
            </div>
          </div>
        ))}
      </div>

      {/* Toolbar */}
      <div className="admin-toolbar" style={{ marginBottom: 24 }}>
        <div className="toolbar-left">
          <div className="admin-form-group" style={{ minWidth: 250 }}>
            <input type="text" placeholder="🔍 Search name, email, phone..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} />
          </div>
          <div className="admin-form-group">
            <select value={filterRole} onChange={(e) => setFilterRole(e.target.value)}>
              <option value="all">All Roles</option>
              <option value="admin">Admin</option>
              <option value="manager">Manager</option>
              <option value="sales">Sales (Employee)</option>
              <option value="accountant">Accountant</option>
            </select>
          </div>
          <span className="text-14 text-gray-500">{filteredMembers.length} members</span>
        </div>
        <div className="toolbar-right">
          <button className="btn btn-primary" onClick={() => { setModalError(""); setModalSuccess(""); setShowAddModal(true); }}>
            + Add New Member
          </button>
        </div>
      </div>

      {/* ========== CREDENTIALS TABLE (Important!) ========== */}
      <div className="data-table-wrapper" style={{ marginBottom: 24 }}>
        <div className="data-table-toolbar">
          <div className="toolbar-title">
            <h3>🔑 Login Credentials (Employee Dashboard Access)</h3>
            <p style={{ fontSize: 12, color: "#9ca3af", margin: "4px 0 0" }}>
              Ye email + password use karke employee login kar sakte hain: yoursite.com/admin
            </p>
          </div>
        </div>
        <div className="data-table-scroll" style={{ maxHeight: 400, overflowY: "auto" }}>
          <table className="data-table">
            <thead>
              <tr>
                <th>Member</th>
                <th>Email (Login ID)</th>
                <th>Password</th>
                <th>Role</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredMembers.map((member) => (
                <tr key={member.id}>
                  <td>
                    <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                      <div className="admin-avatar small">
                        <span>{member.name?.split(" ").map((n) => n[0]).join("")}</span>
                      </div>
                      <div>
                        <div className="font-bold text-14">{member.name}</div>
                        <div className="text-12 text-gray-400">{member.phone || "No phone"}</div>
                      </div>
                    </div>
                  </td>
                  <td>
                    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                      <span style={{ fontSize: 13, fontWeight: 600, fontFamily: "monospace", backgroundColor: "#f0f9ff", padding: "4px 8px", borderRadius: 6 }}>
                        {member.email}
                      </span>
                      <button
                        onClick={() => navigator.clipboard?.writeText(member.email)}
                        style={{ background: "none", border: "none", cursor: "pointer", fontSize: 14 }}
                        title="Copy Email"
                      >
                        📋
                      </button>
                    </div>
                  </td>
                  <td>
                    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                      <span style={{ fontSize: 13, fontWeight: 600, fontFamily: "monospace", backgroundColor: "#fefce8", padding: "4px 8px", borderRadius: 6 }}>
                        {member.password || "••••••"}
                      </span>
                      <button
                        onClick={() => navigator.clipboard?.writeText(member.password || "")}
                        style={{ background: "none", border: "none", cursor: "pointer", fontSize: 14 }}
                        title="Copy Password"
                      >
                        📋
                      </button>
                    </div>
                  </td>
                  <td>
                    <span className={`status-badge badge-${member.role}`}>{member.role}</span>
                  </td>
                  <td>
                    <span className={`status-badge ${member.isActive !== false ? "badge-completed" : "badge-dead"}`}>
                      {member.isActive !== false ? "Active" : "Inactive"}
                    </span>
                  </td>
                  <td>
                    <div style={{ display: "flex", gap: 4 }}>
                      <button className="btn btn-ghost-blue" style={{ fontSize: 11, padding: "4px 8px" }} onClick={() => openViewModal(member)}>👁</button>
                      <button className="btn btn-ghost-green" style={{ fontSize: 11, padding: "4px 8px" }} onClick={() => openEditModal(member)}>✏️</button>
                      <button className="btn btn-ghost-red" style={{ fontSize: 11, padding: "4px 8px" }} onClick={() => openDeleteModal(member)}>🗑</button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* ========== TEAM CARDS ========== */}
      {loading ? (
        <div className="admin-loading"><div className="spinner-wrap"><div className="spinner" /><p className="spinner-text">Loading...</p></div></div>
      ) : filteredMembers.length === 0 ? (
        <div className="text-center text-gray-400" style={{ backgroundColor: "white", borderRadius: 12, padding: 48 }}>
          <p className="text-24 mb-2">👥</p>
          <p>No team members found.</p>
        </div>
      ) : (
        <div className="admin-grid admin-grid-3">
          {filteredMembers.map((member) => (
            <div key={member.id} className={`team-card ${member.isActive === false ? "inactive" : ""}`}>
              <div className={`team-card-header role-${member.role || "sales"}`} />
              <div className="team-card-avatar">
                <div className="avatar-ring">
                  <div className="avatar-inner">
                    <span>{member.name?.split(" ").map((n) => n[0]).join("")}</span>
                  </div>
                </div>
              </div>
              <div className="team-card-body">
                <h4 className="team-name">{member.name}</h4>
                <p className="team-email">{member.email}</p>
                <div className="team-badges">
                  <span className={`status-badge badge-${member.role}`}>{member.role}</span>
                  <span className={`status-badge ${member.isActive !== false ? "badge-completed" : "badge-dead"}`}>
                    {member.isActive !== false ? "Active" : "Inactive"}
                  </span>
                </div>
                <p className="team-phone">📱 {member.phone || "No phone"}</p>
                <div style={{ display: "flex", gap: 6, marginTop: 12, justifyContent: "center" }}>
                  <button className="btn btn-ghost-blue" style={{ fontSize: 11 }} onClick={() => openViewModal(member)}>👁 View</button>
                  <button className="btn btn-ghost-green" style={{ fontSize: 11 }} onClick={() => openEditModal(member)}>✏️ Edit</button>
                  <button className="btn btn-ghost-red" style={{ fontSize: 11 }} onClick={() => openDeleteModal(member)}>🗑 Delete</button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* ==========================================
          MODAL: ADD MEMBER
      ========================================== */}
      {showAddModal && (
        <div className="modal-overlay" onClick={() => setShowAddModal(false)}>
          <div className="modal-box modal-md" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>👤 Add New Team Member</h3>
              <button className="modal-close-btn" onClick={() => setShowAddModal(false)}>✕</button>
            </div>
            <div className="modal-body">
              {modalError && <div style={{ marginBottom: 12, padding: 12, backgroundColor: "#fef2f2", border: "1px solid #fecaca", borderRadius: 8 }}><p style={{ color: "#991b1b", fontSize: 14, margin: 0 }}>❌ {modalError}</p></div>}
              {modalSuccess && <div style={{ marginBottom: 12, padding: 12, backgroundColor: "#f0fdf4", border: "1px solid #bbf7d0", borderRadius: 8 }}><p style={{ color: "#166534", fontSize: 14, margin: 0 }}>{modalSuccess}</p></div>}

              <div className="admin-form-group" style={{ marginBottom: 16 }}>
                <label>Full Name *</label>
                <input type="text" name="name" value={addForm.name} onChange={handleAddChange} placeholder="Enter full name" disabled={modalLoading} />
              </div>

              <div className="form-row">
                <div className="admin-form-group">
                  <label>Email (Login ID) *</label>
                  <input type="email" name="email" value={addForm.email} onChange={handleAddChange} placeholder="employee@sdtech.com" disabled={modalLoading} />
                  <p style={{ fontSize: 11, color: "#9ca3af", margin: "4px 0 0" }}>Employee isi email se login karega</p>
                </div>
                <div className="admin-form-group">
                  <label>Phone Number</label>
                  <input type="tel" name="phone" value={addForm.phone} onChange={handleAddChange} placeholder="+91 9876543210" disabled={modalLoading} />
                </div>
              </div>

              <div className="form-row">
                <div className="admin-form-group">
                  <label>Password *</label>
                  <div style={{ position: "relative" }}>
                    <input
                      type={showAddPassword ? "text" : "password"}
                      name="password"
                      value={addForm.password}
                      onChange={handleAddChange}
                      placeholder="Min 6 characters"
                      disabled={modalLoading}
                      style={{ paddingRight: 40 }}
                    />
                    <button
                      type="button"
                      onClick={() => setShowAddPassword(!showAddPassword)}
                      style={{ position: "absolute", right: 12, top: "50%", transform: "translateY(-50%)", background: "none", border: "none", cursor: "pointer", fontSize: 16 }}
                    >
                      {showAddPassword ? "🙈" : "👁"}
                    </button>
                  </div>
                  <p style={{ fontSize: 11, color: "#9ca3af", margin: "4px 0 0" }}>Employee isi password se login karega</p>

                  {/* Password Strength */}
                  {addForm.password && (() => {
                    const strength = getPasswordStrength(addForm.password);
                    return (
                      <div style={{ marginTop: 6 }}>
                        <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 2 }}>
                          <span style={{ fontSize: 11, color: "#6b7280" }}>Strength:</span>
                          <span style={{ fontSize: 11, fontWeight: 700, color: strength.color }}>{strength.label}</span>
                        </div>
                        <div style={{ width: "100%", height: 4, backgroundColor: "#f3f4f6", borderRadius: 9999, overflow: "hidden" }}>
                          <div style={{ width: strength.width, height: "100%", backgroundColor: strength.color, borderRadius: 9999, transition: "width 0.3s" }} />
                        </div>
                      </div>
                    );
                  })()}
                </div>

                <div className="admin-form-group">
                  <label>Role *</label>
                  <select name="role" value={addForm.role} onChange={handleAddChange} disabled={modalLoading}>
                    <option value="">Select Role</option>
                    <option value="sales">🟢 Sales Executive (Employee)</option>
                    <option value="manager">🔵 Manager</option>
                    <option value="accountant">🟠 Accountant</option>
                    <option value="admin">🔴 Admin</option>
                  </select>
                </div>
              </div>

              {/* Role Description */}
              {addForm.role && (
                <div style={{ marginTop: 12, padding: 12, backgroundColor: "#f0f9ff", border: "1px solid #bfdbfe", borderRadius: 8 }}>
                  <p style={{ margin: 0, fontSize: 12, color: "#374151" }}>{roleDescriptions[addForm.role]}</p>
                </div>
              )}

              {/* Login Info Preview */}
              {addForm.email && addForm.password && addForm.role && (
                <div style={{ marginTop: 16, padding: 16, backgroundColor: "#f0fdf4", border: "1px solid #bbf7d0", borderRadius: 8 }}>
                  <h4 style={{ margin: "0 0 8px", fontSize: 13, color: "#166534" }}>🔑 Employee Login Details:</h4>
                  <div style={{ fontFamily: "monospace", fontSize: 13, lineHeight: 2 }}>
                    <div>URL: <strong>yoursite.com/admin</strong></div>
                    <div>Email: <strong style={{ color: "#2563eb" }}>{addForm.email}</strong></div>
                    <div>Password: <strong style={{ color: "#2563eb" }}>{addForm.password}</strong></div>
                    <div>Access: <strong>{addForm.role === "sales" ? "Employee Dashboard (sirf apne tasks)" : addForm.role}</strong></div>
                  </div>
                </div>
              )}
            </div>
            <div className="modal-footer">
              <button className="btn btn-outline" onClick={() => setShowAddModal(false)} disabled={modalLoading}>Cancel</button>
              <button className="btn btn-primary" onClick={handleAddMember} disabled={modalLoading}>
                {modalLoading ? "⏳ Adding..." : "👤 Add Member"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ==========================================
          MODAL: EDIT MEMBER
      ========================================== */}
      {showEditModal && (
        <div className="modal-overlay" onClick={() => setShowEditModal(false)}>
          <div className="modal-box modal-md" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>✏️ Edit Team Member</h3>
              <button className="modal-close-btn" onClick={() => setShowEditModal(false)}>✕</button>
            </div>
            <div className="modal-body">
              {modalError && <div style={{ marginBottom: 12, padding: 12, backgroundColor: "#fef2f2", border: "1px solid #fecaca", borderRadius: 8 }}><p style={{ color: "#991b1b", fontSize: 14, margin: 0 }}>❌ {modalError}</p></div>}
              {modalSuccess && <div style={{ marginBottom: 12, padding: 12, backgroundColor: "#f0fdf4", border: "1px solid #bbf7d0", borderRadius: 8 }}><p style={{ color: "#166534", fontSize: 14, margin: 0 }}>{modalSuccess}</p></div>}

              <div className="admin-form-group" style={{ marginBottom: 16 }}>
                <label>Full Name *</label>
                <input type="text" name="name" value={editForm.name} onChange={handleEditChange} disabled={modalLoading} />
              </div>

              <div className="form-row">
                <div className="admin-form-group">
                  <label>Email (Login ID) *</label>
                  <input type="email" name="email" value={editForm.email} onChange={handleEditChange} disabled={modalLoading} />
                </div>
                <div className="admin-form-group">
                  <label>Phone Number</label>
                  <input type="tel" name="phone" value={editForm.phone} onChange={handleEditChange} placeholder="+91 9876543210" disabled={modalLoading} />
                </div>
              </div>

              <div className="form-row">
                <div className="admin-form-group">
                  <label>Password</label>
                  <div style={{ position: "relative" }}>
                    <input
                      type={showEditPassword ? "text" : "password"}
                      name="password"
                      value={editForm.password}
                      onChange={handleEditChange}
                      placeholder="Enter new password"
                      disabled={modalLoading}
                      style={{ paddingRight: 40 }}
                    />
                    <button
                      type="button"
                      onClick={() => setShowEditPassword(!showEditPassword)}
                      style={{ position: "absolute", right: 12, top: "50%", transform: "translateY(-50%)", background: "none", border: "none", cursor: "pointer", fontSize: 16 }}
                    >
                      {showEditPassword ? "🙈" : "👁"}
                    </button>
                  </div>
                  <p style={{ fontSize: 11, color: "#9ca3af", margin: "4px 0 0" }}>Naya password dalein toh Firebase mein update karein</p>
                </div>

                <div className="admin-form-group">
                  <label>Role *</label>
                  <select name="role" value={editForm.role} onChange={handleEditChange} disabled={modalLoading}>
                    <option value="sales">🟢 Sales Executive (Employee)</option>
                    <option value="manager">🔵 Manager</option>
                    <option value="accountant">🟠 Accountant</option>
                    <option value="admin">🔴 Admin</option>
                  </select>
                </div>
              </div>

              <div className="admin-form-group" style={{ marginBottom: 16 }}>
                <label>Status</label>
                <select
                  value={editForm.isActive ? "true" : "false"}
                  onChange={(e) => setEditForm((prev) => ({ ...prev, isActive: e.target.value === "true" }))}
                  disabled={modalLoading}
                >
                  <option value="true">✅ Active</option>
                  <option value="false">⛔ Inactive</option>
                </select>
              </div>

              {/* Role Description */}
              {editForm.role && (
                <div style={{ padding: 12, backgroundColor: "#f0f9ff", border: "1px solid #bfdbfe", borderRadius: 8 }}>
                  <p style={{ margin: 0, fontSize: 12, color: "#374151" }}>{roleDescriptions[editForm.role]}</p>
                </div>
              )}
            </div>
            <div className="modal-footer">
              <button className="btn btn-outline" onClick={() => setShowEditModal(false)} disabled={modalLoading}>Cancel</button>
              <button className="btn btn-primary" onClick={handleEditMember} disabled={modalLoading}>
                {modalLoading ? "⏳ Saving..." : "💾 Save Changes"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ==========================================
          MODAL: VIEW MEMBER
      ========================================== */}
      {showViewModal && viewMember && (
        <div className="modal-overlay" onClick={() => setShowViewModal(false)}>
          <div className="modal-box modal-sm" onClick={(e) => e.stopPropagation()}>
            <div style={{ width: "100%", height: 80, background: `linear-gradient(135deg, ${viewMember.role === "admin" ? "#a855f7, #9333ea" : viewMember.role === "manager" ? "#3b82f6, #2563eb" : viewMember.role === "sales" ? "#22c55e, #16a34a" : "#f97316, #ea580c"})`, position: "relative", borderRadius: "16px 16px 0 0" }}>
              <button className="modal-close-btn" style={{ position: "absolute", top: 8, right: 8, color: "white", background: "rgba(0,0,0,0.2)" }} onClick={() => setShowViewModal(false)}>✕</button>
            </div>

            <div style={{ padding: "0 24px", marginTop: -32, textAlign: "center" }}>
              <div className="avatar-ring" style={{ width: 64, height: 64, background: "white", borderRadius: 9999, padding: 2, boxShadow: "0 4px 8px rgba(0,0,0,0.1)", margin: "0 auto 8px" }}>
                <div className="avatar-inner" style={{ width: "100%", height: "100%", background: "#dbeafe", borderRadius: 9999, display: "flex", alignItems: "center", justifyContent: "center" }}>
                  <span style={{ color: "#2563eb", fontWeight: 700, fontSize: 18 }}>{viewMember.name?.split(" ").map((n) => n[0]).join("")}</span>
                </div>
              </div>
              <h3 style={{ margin: 0, fontSize: 18 }}>{viewMember.name}</h3>
              <p style={{ margin: "4px 0", fontSize: 14, color: "#9ca3af" }}>{viewMember.email}</p>
            </div>

            <div className="modal-body" style={{ padding: 24 }}>
              {/* Badges */}
              <div style={{ display: "flex", justifyContent: "center", gap: 8, marginBottom: 16 }}>
                <span className={`status-badge badge-${viewMember.role}`}>{viewMember.role}</span>
                <span className={`status-badge ${viewMember.isActive !== false ? "badge-completed" : "badge-dead"}`}>
                  {viewMember.isActive !== false ? "Active" : "Inactive"}
                </span>
              </div>

              {/* 🔑 Login Credentials Box */}
              <div style={{
                padding: 16,
                backgroundColor: "#f0f9ff",
                border: "2px solid #3b82f6",
                borderRadius: 12,
                marginBottom: 16,
              }}>
                <h4 style={{ margin: "0 0 12px", fontSize: 14, color: "#1e40af", fontWeight: 700 }}>
                  🔑 Employee Login Credentials
                </h4>

                {/* URL */}
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "8px 0", borderBottom: "1px solid #bfdbfe" }}>
                  <span style={{ fontSize: 12, color: "#6b7280" }}>Login URL</span>
                  <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    <span style={{ fontSize: 12, fontWeight: 700, fontFamily: "monospace" }}>yoursite.com/admin</span>
                  </div>
                </div>

                {/* Email */}
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "8px 0", borderBottom: "1px solid #bfdbfe" }}>
                  <span style={{ fontSize: 12, color: "#6b7280" }}>Email</span>
                  <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    <span style={{ fontSize: 13, fontWeight: 700, fontFamily: "monospace", color: "#2563eb" }}>{viewMember.email}</span>
                    <button onClick={() => navigator.clipboard?.writeText(viewMember.email)} style={{ background: "none", border: "none", cursor: "pointer", fontSize: 14 }} title="Copy">📋</button>
                  </div>
                </div>

                {/* Password */}
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "8px 0" }}>
                  <span style={{ fontSize: 12, color: "#6b7280" }}>Password</span>
                  <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    <span style={{ fontSize: 13, fontWeight: 700, fontFamily: "monospace", color: "#2563eb" }}>
                      {showViewPassword ? viewMember.password || "••••••" : "••••••••"}
                    </span>
                    <button onClick={() => setShowViewPassword(!showViewPassword)} style={{ background: "none", border: "none", cursor: "pointer", fontSize: 14 }} title={showViewPassword ? "Hide" : "Show"}>
                      {showViewPassword ? "🙈" : "👁"}
                    </button>
                    <button onClick={() => navigator.clipboard?.writeText(viewMember.password || "")} style={{ background: "none", border: "none", cursor: "pointer", fontSize: 14 }} title="Copy">📋</button>
                  </div>
                </div>

                {/* Copy All Button */}
                <button
                  onClick={() => {
                    const text = `SDtech Solutions - Employee Login\nURL: yoursite.com/admin\nEmail: ${viewMember.email}\nPassword: ${viewMember.password || "••••••"}`;
                    navigator.clipboard?.writeText(text);
                  }}
                  style={{
                    width: "100%",
                    marginTop: 12,
                    padding: "8px 16px",
                    backgroundColor: "#2563eb",
                    color: "white",
                    border: "none",
                    borderRadius: 8,
                    fontSize: 12,
                    fontWeight: 700,
                    cursor: "pointer",
                  }}
                >
                  📋 Copy All Credentials
                </button>
              </div>

              {/* Details */}
              <div style={{ backgroundColor: "#f9fafb", borderRadius: 8, padding: 16 }}>
                <div style={{ display: "flex", justifyContent: "space-between", padding: "6px 0", borderBottom: "1px solid #f3f4f6" }}>
                  <span style={{ fontSize: 12, color: "#6b7280" }}>Phone</span>
                  <span style={{ fontSize: 12, fontWeight: 600 }}>{viewMember.phone || "—"}</span>
                </div>
                <div style={{ display: "flex", justifyContent: "space-between", padding: "6px 0" }}>
                  <span style={{ fontSize: 12, color: "#6b7280" }}>Role</span>
                  <span className={`status-badge badge-${viewMember.role}`} style={{ fontSize: 11 }}>{viewMember.role}</span>
                </div>
              </div>
            </div>

            <div className="modal-footer">
              <button className="btn btn-outline" onClick={() => setShowViewModal(false)}>Close</button>
              <button className="btn btn-primary" onClick={() => { setShowViewModal(false); openEditModal(viewMember); }}>✏️ Edit</button>
            </div>
          </div>
        </div>
      )}

      {/* ==========================================
          MODAL: DELETE
      ========================================== */}
      {showDeleteModal && memberToDelete && (
        <div className="modal-overlay" onClick={() => setShowDeleteModal(false)}>
          <div className="modal-box modal-sm" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header" style={{ borderBottom: "1px solid #fecaca" }}>
              <h3 style={{ color: "#dc2626" }}>🗑️ Delete Member</h3>
              <button className="modal-close-btn" onClick={() => setShowDeleteModal(false)}>✕</button>
            </div>
            <div className="modal-body" style={{ textAlign: "center" }}>
              <div style={{ fontSize: 48, marginBottom: 12 }}>⚠️</div>
              <h4 style={{ fontSize: 16, fontWeight: 700, margin: "0 0 8px" }}>Are you sure?</h4>
              <p style={{ fontSize: 14, color: "#6b7280" }}>
                <strong style={{ color: "#dc2626" }}>"{memberToDelete.name}"</strong> delete ho jayega.
                Unki saari data bhi delete ho jayegi.
              </p>
            </div>
            <div className="modal-footer">
              <button className="btn btn-outline" onClick={() => setShowDeleteModal(false)} disabled={modalLoading}>Cancel</button>
              <button className="btn btn-danger" onClick={handleDeleteMember} disabled={modalLoading}>
                {modalLoading ? "⏳ Deleting..." : "🗑️ Yes, Delete"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TeamPage;